import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import {
  RiDashboardLine, RiCalendarCheckLine, RiTeamLine,
  RiScissorsFill, RiUserLine, RiBarChartLine,
  RiSettings3Line, RiLogoutBoxLine, RiMenuLine, RiCloseLine,
  RiCoupon3Line,
} from 'react-icons/ri'

const nav = [
  { to: '/admin',              icon: RiDashboardLine,    label: 'Dashboard',     end: true },
  { to: '/admin/appointments', icon: RiCalendarCheckLine, label: 'Appointments' },
  { to: '/admin/staff',        icon: RiTeamLine,         label: 'Staff' },
  { to: '/admin/services',     icon: RiScissorsFill,     label: 'Services' },
  { to: '/admin/customers',    icon: RiUserLine,         label: 'Customers' },
  { to: '/admin/coupons',      icon: RiCoupon3Line,      label: 'Coupons' },
  { to: '/admin/reports',      icon: RiBarChartLine,     label: 'Reports' },
  { to: '/admin/settings',     icon: RiSettings3Line,    label: 'Settings' },
]

export default function AdminLayout() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)

  const handleLogout = async () => { await logout(); navigate('/') }

  const SidebarItem = ({ item }) => (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={() => setOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-xl font-body font-medium text-sm
         transition-all duration-200 ${
          isActive
            ? 'bg-salon-primary text-white shadow-salon-sm'
            : 'text-salon-cream/70 hover:bg-white/10 hover:text-white'
        }`
      }
    >
      <item.icon className="text-xl shrink-0" />
      {item.label}
    </NavLink>
  )

  const Sidebar = () => (
    <div className="h-full flex flex-col">
      {/* Logo */}
      <div className="px-6 pt-8 pb-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-salon-primary flex items-center justify-center shadow-salon-sm">
            <RiScissorsFill className="text-white text-lg" />
          </div>
          <div>
            <p className="font-display font-bold text-white text-lg leading-none">Salon Vora</p>
            <p className="text-xs text-salon-cream/50 font-body mt-0.5">Admin Panel</p>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
        {nav.map((item) => <SidebarItem key={item.to} item={item} />)}
      </nav>

      {/* User */}
      <div className="px-4 pb-6 border-t border-white/10 pt-4">
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 mb-2">
          <div className="w-8 h-8 rounded-full bg-salon-primary flex items-center justify-center text-white text-sm font-bold">
            {user?.name?.charAt(0) || 'A'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-semibold font-body truncate">{user?.name}</p>
            <p className="text-salon-cream/50 text-xs font-body">Administrator</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-salon-cream/60
                     hover:text-white hover:bg-white/10 transition-all duration-200 font-body text-sm"
        >
          <RiLogoutBoxLine className="text-lg" /> Logout
        </button>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen flex bg-salon-cream">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-salon-dark flex-col fixed inset-y-0 left-0 z-30">
        <Sidebar />
      </aside>

      {/* Mobile overlay */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-salon-dark">
            <Sidebar />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="bg-white border-b border-salon-cream-dark px-6 py-4 flex items-center gap-4 sticky top-0 z-20">
          <button
            className="lg:hidden p-2 rounded-lg text-salon-muted hover:bg-salon-cream"
            onClick={() => setOpen(!open)}
          >
            {open ? <RiCloseLine className="text-xl" /> : <RiMenuLine className="text-xl" />}
          </button>
          <h1 className="font-display text-salon-dark font-semibold text-xl">Admin Dashboard</h1>
        </header>

        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
